import { Stack, Typography, SxProps, Theme } from '@mui/material';
import { RouterLink } from 'common/ui/RouterLink';
import { Form, FormRef } from 'common/form';
import { Header, HeaderBreadcrumb } from 'common/ui/Header';
import { CampaignType, Channels } from 'generated-graphql/types';
import { usePortalUrl } from 'modules/portal/hooks/usePortalUrl';
import {
  sourcingSharedSetupApplicationSchema,
  getSourcingSharedSetupApplicationFields,
  SourcingSharedSetupApplicationFormData,
  getSourcingSharedCampaignTypeDefaultValue,
  shouldSourcingSharedShowCustomJobPostingUrl,
  getSourcingSharedCustomJobPostingUrl,
  getSourcingSharedCampaignTypeOptions,
  isSourcingSharedUrlOnlyMode,
  isSourcingSharedUrlPreviewMode,
  isSourcingSharedUrlAddMode,
  getSourcingSharedPreviewUrl
} from 'modules/sourcing-shared/forms';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { TrackingEventName } from 'services/tracking/constants';
import { useTracking } from 'services/tracking/hooks';

const CHOOSE_APPLICATION_METHOD_TITLE = 'Choose application method';
const ADD_APPLICATION_LINK_TITLE = 'Add application link';
const PREVIEW_APPLICATION_LINK_TITLE = 'Preview application link';

const previewLinkTextSx: SxProps<Theme> = {
  fontSize: '16px',
  color: 'text.primary'
};

type GetPageTitleProps = {
  isUrlAddMode: boolean;
  isUrlPreviewMode: boolean;
};

const getPageTitle = ({
  isUrlAddMode,
  isUrlPreviewMode
}: GetPageTitleProps) => {
  if (isUrlPreviewMode) {
    return PREVIEW_APPLICATION_LINK_TITLE;
  }
  if (isUrlAddMode) {
    return ADD_APPLICATION_LINK_TITLE;
  }
  return CHOOSE_APPLICATION_METHOD_TITLE;
};

type SourcingSharedSetupApplicationStepProps = {
  headerBreadcrumbs: HeaderBreadcrumb[];
  candidateBatchId: string;
  hasCandidateBatchAtsJobId: boolean;
  candidateBatchAtsJobPostingUrl: string | undefined | null;
  candidateBatchCustomJobPostingUrl: string | undefined | null;
  defaultFormData: SourcingSharedSetupApplicationFormData | null;
  onSetupApplicationBack: () => void;
  onSetupApplicationNext: (
    formData: SourcingSharedSetupApplicationFormData
  ) => void;
  channelType: Channels;
};

export const SourcingSharedSetupApplicationStep: React.FC<
  SourcingSharedSetupApplicationStepProps
> = ({
  headerBreadcrumbs,
  candidateBatchId,
  hasCandidateBatchAtsJobId,
  candidateBatchAtsJobPostingUrl,
  candidateBatchCustomJobPostingUrl,
  defaultFormData,
  onSetupApplicationBack,
  onSetupApplicationNext,
  channelType
}) => {
  const formRef = useRef<FormRef<SourcingSharedSetupApplicationFormData>>(null);
  const [hasAttemptedPortalUrlGeneration, setHasAttemptedPortalUrlGeneration] =
    useState(false);
  const integrationCredentials = useRecoilValue(integrationAtom);
  const { openModal } = useModalV2();
  const { track } = useTracking();

  const { portalUrl, isGeneratingPortalUrl, onGeneratePortalUrl } =
    usePortalUrl({
      candidateBatchId
    });

  const [campaignType, setCampaignType] = useState<CampaignType>(
    getSourcingSharedCampaignTypeDefaultValue({
      applicationFormData: defaultFormData,
      hasAtsJobId: hasCandidateBatchAtsJobId,
      hasAtsJobApplicationUrl: !!candidateBatchAtsJobPostingUrl
    })
  );

  // Compute campaign type options for mode detection
  const campaignTypeOptions = useMemo(
    () =>
      getSourcingSharedCampaignTypeOptions({
        hasAtsJobId: hasCandidateBatchAtsJobId,
        hasIntegrationCredentials: !!integrationCredentials,
        atsProvider: integrationCredentials?.provider,
        portalUrl,
        customJobPostingUrl: candidateBatchCustomJobPostingUrl,
        atsJobApplicationUrl: candidateBatchAtsJobPostingUrl,
        channelType
      }),
    [
      hasCandidateBatchAtsJobId,
      integrationCredentials,
      portalUrl,
      candidateBatchCustomJobPostingUrl,
      candidateBatchAtsJobPostingUrl,
      channelType
    ]
  );

  // Detect URL-only mode and its sub-modes
  const isUrlOnlyMode = isSourcingSharedUrlOnlyMode(campaignTypeOptions);
  const isUrlPreviewMode = isSourcingSharedUrlPreviewMode(
    campaignTypeOptions,
    portalUrl,
    candidateBatchCustomJobPostingUrl
  );
  const isUrlAddMode = isSourcingSharedUrlAddMode(
    campaignTypeOptions,
    portalUrl,
    candidateBatchCustomJobPostingUrl
  );

  // Get the URL to preview when in preview mode
  const previewUrl = getSourcingSharedPreviewUrl(
    portalUrl,
    candidateBatchCustomJobPostingUrl
  );

  // Dynamic page title based on mode
  const pageTitle = getPageTitle({ isUrlAddMode, isUrlPreviewMode });

  useEffect(() => {
    if (hasCandidateBatchAtsJobId || candidateBatchAtsJobPostingUrl) {
      // No need to generate portal URL - mark as attempted so skip logic can proceed
      setHasAttemptedPortalUrlGeneration(true);
      return;
    }
    onGeneratePortalUrl();
    setHasAttemptedPortalUrlGeneration(true);
  }, [
    candidateBatchId,
    hasCandidateBatchAtsJobId,
    candidateBatchAtsJobPostingUrl,
    onGeneratePortalUrl
  ]);

  // In URL-only mode, ensure the form has the correct campaignType value
  // since the radio field is hidden and won't be submitted otherwise
  useEffect(() => {
    if (!isUrlOnlyMode) return;
    setCampaignType(CampaignType.CustomJobApplicationUrlOutreach);
    if (formRef.current) {
      formRef.current.setValue(
        'campaignType',
        CampaignType.CustomJobApplicationUrlOutreach
      );
    }
  }, [isUrlOnlyMode]);

  // Skip this step ONLY if the only option is "Uploading CV in conversation"
  useEffect(() => {
    // Wait for portal URL generation to be attempted before deciding to skip
    // This prevents skipping on first render before generation has started
    if (!hasAttemptedPortalUrlGeneration) return;

    // Wait for portal URL generation to complete
    if (isGeneratingPortalUrl) return;

    // Only skip if there's exactly ONE option and it's RequestCvOutreach
    // Do NOT skip if there are multiple options
    const shouldSkipStep =
      campaignTypeOptions.length === 1 &&
      campaignTypeOptions[0].value === CampaignType.RequestCvOutreach;

    if (shouldSkipStep) {
      // Auto-proceed to next step with RequestCvOutreach
      onSetupApplicationNext({
        campaignType: CampaignType.RequestCvOutreach,
        customJobPostingUrl: undefined
      });
    }
  }, [
    hasAttemptedPortalUrlGeneration,
    isGeneratingPortalUrl,
    campaignTypeOptions,
    onSetupApplicationNext
  ]);

  useEffect(() => {
    if (!formRef.current) return;

    const { unsubscribe } = formRef.current.watch(
      (value: SourcingSharedSetupApplicationFormData, { name }: any) => {
        if (name === 'campaignType') {
          setCampaignType(value.campaignType as CampaignType);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!formRef.current) return;
    const shouldShowUrl = shouldSourcingSharedShowCustomJobPostingUrl({
      portalUrl,
      hasCustomJobPostingUrl: !!candidateBatchCustomJobPostingUrl,
      campaignType
    });

    if (!shouldShowUrl) {
      formRef.current.setValue('customJobPostingUrl', undefined);
    } else if (defaultFormData?.customJobPostingUrl) {
      formRef.current.setValue(
        'customJobPostingUrl',
        defaultFormData.customJobPostingUrl
      );
    }
  }, [
    portalUrl,
    candidateBatchCustomJobPostingUrl,
    defaultFormData?.customJobPostingUrl,
    campaignType
  ]);

  const handleGoNextStep = async (
    data: SourcingSharedSetupApplicationFormData
  ) => {
    const { showCustomJobPostingUrlError, customJobPostingUrl } =
      getSourcingSharedCustomJobPostingUrl({
        campaignType,
        portalUrl,
        atsJobPostingUrl: candidateBatchAtsJobPostingUrl,
        candidateBatchCustomJobPostingUrl: candidateBatchCustomJobPostingUrl,
        formCustomJobPostingUrl: data.customJobPostingUrl
      });

    if (showCustomJobPostingUrlError) {
      openModal({
        title: 'Application URL is required',
        subtitle: 'Please enter the application URL for the role.',
        primaryButtonText: 'OK',
        primaryButtonProps: { variant: 'primary' }
      });
      return;
    }

    track(TrackingEventName.SourcingInviteChooseApplicationUrl, {
      value: campaignType,
      candidateBatchId
    });

    onSetupApplicationNext({
      campaignType: campaignType,
      customJobPostingUrl
    });
  };

  const handleFormSubmit = async (
    data: SourcingSharedSetupApplicationFormData
  ) => {
    if (defaultFormData && defaultFormData.campaignType !== data.campaignType) {
      openModal({
        title: 'Change application method?',
        subtitle:
          'Any edits made to the opening message template will be lost.',
        primaryButtonText: 'Cancel',
        secondaryButtonText: 'Confirm',
        secondaryButtonProps: {
          state: 'destructive'
        },
        onSecondaryAction: () => handleGoNextStep(data)
      });
    } else {
      await handleGoNextStep(data);
    }
  };

  return (
    <>
      <Header
        title={pageTitle}
        breadcrumbs={headerBreadcrumbs}
        currentBreadcrumb={pageTitle}
        onBack={onSetupApplicationBack}
      />
      <Stack direction="column" spacing="40px">
        <Stack direction="column" spacing="40px" maxWidth="700px">
          {isUrlPreviewMode && previewUrl && (
            <Typography sx={previewLinkTextSx}>
              <RouterLink
                to={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Preview the link
              </RouterLink>
              {' that candidates will use to apply for this job.'}
            </Typography>
          )}
          <Form
            ref={formRef}
            schema={sourcingSharedSetupApplicationSchema}
            fields={getSourcingSharedSetupApplicationFields({
              campaignType,
              defaultCustomJobPostingUrl: defaultFormData?.customJobPostingUrl,
              hasAtsJobId: hasCandidateBatchAtsJobId,
              atsJobApplicationUrl: candidateBatchAtsJobPostingUrl,
              atsProvider: integrationCredentials?.provider,
              hasIntegrationCredentials: !!integrationCredentials,
              portalUrl,
              customJobPostingUrl: candidateBatchCustomJobPostingUrl,
              isGeneratingPortalUrl,
              channelType
            })}
            submitButtonProps={{
              label: 'Next step'
            }}
            onSubmit={handleFormSubmit}
          />
        </Stack>
      </Stack>
    </>
  );
};
